import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { API } from "../../config";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { Autoplay, Navigation, Pagination } from "swiper/modules";

interface Client {
  client_id: number;
  name: string;
  logo_url: string | null;
  website_url: string | null;
  display_order: number | null;
  record_status_id: number;
}

export const OurClientsSlider = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/clients`)
      .then((res) => res.json())
      .then((data) => {
        const active = (data.clients as Client[]).filter(
          (c) => c.record_status_id === 1
        );
        active.sort((a, b) => {
          if (a.display_order == null && b.display_order == null) return 0;
          if (a.display_order == null) return 1;
          if (b.display_order == null) return -1;
          return a.display_order - b.display_order;
        });
        setClients(active);
      })
      .catch((err) => console.error("Failed to load clients:", err))
      .finally(() => setLoaded(true));
  }, []);

  return (
    <section id="courses" className="py-10 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="heading-bold headingcolor mb-4">Our Clients</h2>
          <p className="subheading max-w-3xl mx-auto">
            Trusted by leading educational institutions across the region to
            deliver exceptional phonics and language learning programs.
          </p>
        </div>
        {loaded && clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300 mb-4" aria-hidden="true">
              <rect width="20" height="14" x="2" y="7" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
            <h3 className="text-lg font-semibold text-gray-500 mb-1">No clients available at the moment</h3>
            <p className="text-sm text-gray-600">Our client partnerships will be showcased here soon.</p>
          </div>
        ) : (
        <div className="relative">
          <div className="overflow-x-hidden swiper ourclientsSwiper1">
            <div className="wrapper px-2">
              <Swiper
                modules={[Autoplay, Pagination, Navigation]}
                loop
                watchOverflow={false}
                navigation={{ nextEl: ".arrow-right", prevEl: ".arrow-left" }}
                pagination={{ el: ".ourclient-pagination", clickable: true }}
                className="ourclientsSwiper"
                spaceBetween={30}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                slidesPerView={4}
                breakpoints={{
                  320: { slidesPerView: 2, spaceBetween: 15 },
                  640: { slidesPerView: 2, spaceBetween: 20 },
                  768: { slidesPerView: 3, spaceBetween: 25 },
                  1024: { slidesPerView: 5, spaceBetween: 30 },
                  1280: { slidesPerView: 5, spaceBetween: 30 },
                }}
              >
                {clients.map((client) => (
                  <SwiperSlide key={client.client_id}>
                    <div>
                      <div className="md:client-card h-[120px] md:h-[168px] flex items-center justify-center p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 group">
                        <div className="text-center">
                          {client.logo_url ? (
                            <div className="mb-3 overflow-hidden rounded-lg">
                              <img
                                src={client.logo_url}
                                alt={client.name}
                                width={160}
                                height={100}
                                loading="lazy"
                                decoding="async"
                                className="object-contain group-hover:scale-105 transition-transform duration-300 max-h-[100px] mx-auto"
                              />
                            </div>
                          ) : (
                            <div className="mb-3 h-[80px] flex items-center justify-center text-gray-300 text-4xl font-bold">
                              LOGO
                            </div>
                          )}
                          {client.website_url ? (
                            <a
                              href={client.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-gray-700 group-hover:text-red-600 transition-colors"
                            >
                              {client.name}
                            </a>
                          ) : (
                            <p className="text-sm text-gray-700 group-hover:text-red-600 transition-colors">
                              {client.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

            </div>
            <button className="arrow-left arrow" aria-label="Previous clients"><b aria-hidden="true">❮</b></button>
            <button className="arrow-right arrow" aria-label="Next clients"><b aria-hidden="true">❯</b></button>
          </div>
          <div className="ourclient-pagination mt-4 text-center"></div>
        </div>
        )}
        <div className="text-center mt-2">
          <p>Trusted by leading educational institutions and schools</p>
        </div>
      </div>
    </section>
  );
};
